import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Settings2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import {
  useUpsertPackage, useUploadPackageDocument, useCreatePayment, useCreateInvoice, useCreateContract,
} from "@/lib/commercialQueries";

/**
 * Master v5 Prompt 14 — coach-only manual entry surface. Every record
 * here is created/uploaded by hand for now (provider-neutral by
 * design: nothing below assumes a CRM or payment processor — swapping
 * in an automated source later only changes what CALLS these same
 * backend endpoints, never the endpoints themselves).
 */
export function CoachManagementPanel({ workspaceId, pkg, currentContractId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-dashed border-ink/20 rounded-lg p-4 bg-ink/[0.02]" data-testid="commercial-coach-panel">
      <button
        onClick={() => setOpen((v) => !v)} className="flex items-center gap-1.5 text-sm font-medium text-ink"
        data-testid="commercial-coach-panel-toggle"
      >
        <Settings2 size={14} /> Manage Commercial Center {open ? "▾" : "▸"}
      </button>
      {open && (
        <div className="mt-3 space-y-4">
          <PackageForm workspaceId={workspaceId} pkg={pkg} />
          <PaymentForm workspaceId={workspaceId} />
          <InvoiceForm workspaceId={workspaceId} />
          <ContractForm workspaceId={workspaceId} currentContractId={currentContractId} />
        </div>
      )}
    </div>
  );
}

function PackageForm({ workspaceId, pkg }) {
  const [title, setTitle] = useState(pkg?.title || "");
  const [description, setDescription] = useState(pkg?.description || "");
  const [priceAmount, setPriceAmount] = useState(pkg?.price_amount ?? "");
  const [billingCadence, setBillingCadence] = useState(pkg?.billing_cadence || "");
  const fileRef = useRef(null);
  const upsert = useUpsertPackage(workspaceId);
  const uploadDoc = useUploadPackageDocument(workspaceId);

  async function handleSave() {
    try {
      await upsert.mutateAsync({ title, description, price_amount: priceAmount === "" ? null : Number(priceAmount), price_currency: "USD", billing_cadence: billingCadence });
      toast.success("Package saved");
    } catch (e) { toast.error(e.body?.detail || "Couldn't save the package"); }
  }
  async function handleUpload() {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    try { await uploadDoc.mutateAsync(file); toast.success("Programme document uploaded"); } catch (e) { toast.error(e.body?.detail || "Upload failed"); }
  }

  return (
    <div className="space-y-2" data-testid="commercial-coach-package-form">
      <p className="text-xs font-semibold text-ink-soft uppercase tracking-wide">Package / Programme</p>
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" data-testid="commercial-coach-package-title" />
      <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" rows={2} data-testid="commercial-coach-package-description" />
      <div className="grid grid-cols-2 gap-2">
        <Input type="number" value={priceAmount} onChange={(e) => setPriceAmount(e.target.value)} placeholder="Price (USD)" data-testid="commercial-coach-package-price" />
        <Input value={billingCadence} onChange={(e) => setBillingCadence(e.target.value)} placeholder="Billing cadence" data-testid="commercial-coach-package-cadence" />
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={handleSave} disabled={!title || upsert.isPending} data-testid="commercial-coach-package-save-btn">
          {upsert.isPending ? <Loader2 size={13} className="animate-spin" /> : "Save"}
        </Button>
        <input ref={fileRef} type="file" accept="application/pdf" className="text-xs" data-testid="commercial-coach-package-file-input" />
        <Button size="sm" variant="outline" onClick={handleUpload} disabled={uploadDoc.isPending} data-testid="commercial-coach-package-upload-btn">Upload document</Button>
      </div>
    </div>
  );
}

function PaymentForm({ workspaceId }) {
  const [amount, setAmount] = useState(""); const [description, setDescription] = useState("");
  const [status, setStatus] = useState("pending"); const [dueAt, setDueAt] = useState("");
  const create = useCreatePayment(workspaceId);

  async function handleCreate() {
    try {
      await create.mutateAsync({ amount: Number(amount), currency: "USD", status, description, due_at: dueAt ? new Date(dueAt).toISOString() : null });
      setAmount(""); setDescription(""); setDueAt("");
      toast.success("Payment recorded");
    } catch (e) { toast.error(e.body?.detail || "Couldn't record the payment"); }
  }

  return (
    <div className="space-y-2" data-testid="commercial-coach-payment-form">
      <p className="text-xs font-semibold text-ink-soft uppercase tracking-wide">Add Payment</p>
      <div className="grid grid-cols-3 gap-2">
        <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" data-testid="commercial-coach-payment-amount" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-ink/20 rounded-md text-sm px-2" data-testid="commercial-coach-payment-status">
          <option value="pending">Pending</option><option value="paid">Paid</option><option value="failed">Failed</option><option value="refunded">Refunded</option>
        </select>
        <Input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} data-testid="commercial-coach-payment-due" />
      </div>
      <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" data-testid="commercial-coach-payment-description" />
      <Button size="sm" onClick={handleCreate} disabled={!amount || create.isPending} data-testid="commercial-coach-payment-create-btn">
        {create.isPending ? <Loader2 size={13} className="animate-spin" /> : "Add payment"}
      </Button>
    </div>
  );
}

function InvoiceForm({ workspaceId }) {
  const [recordType, setRecordType] = useState("invoice"); const [amount, setAmount] = useState("");
  const [status, setStatus] = useState("issued"); const [issuedAt, setIssuedAt] = useState("");
  const fileRef = useRef(null);
  const create = useCreateInvoice(workspaceId);

  async function handleCreate() {
    const file = fileRef.current?.files?.[0];
    if (!file || !amount || !issuedAt) return;
    try {
      await create.mutateAsync({ file, record_type: recordType, amount, currency: "USD", status, issued_at: new Date(issuedAt).toISOString() });
      setAmount(""); setIssuedAt(""); if (fileRef.current) fileRef.current.value = "";
      toast.success(`${recordType === "invoice" ? "Invoice" : "Receipt"} added`);
    } catch (e) { toast.error(e.body?.detail || "Couldn't add that document"); }
  }

  return (
    <div className="space-y-2" data-testid="commercial-coach-invoice-form">
      <p className="text-xs font-semibold text-ink-soft uppercase tracking-wide">Add Invoice / Receipt</p>
      <div className="grid grid-cols-3 gap-2">
        <select value={recordType} onChange={(e) => setRecordType(e.target.value)} className="border border-ink/20 rounded-md text-sm px-2" data-testid="commercial-coach-invoice-type">
          <option value="invoice">Invoice</option><option value="receipt">Receipt</option>
        </select>
        <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" data-testid="commercial-coach-invoice-amount" />
        <Input type="date" value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)} data-testid="commercial-coach-invoice-issued-at" />
      </div>
      <input ref={fileRef} type="file" accept="application/pdf" className="text-xs" data-testid="commercial-coach-invoice-file-input" />
      <Button size="sm" onClick={handleCreate} disabled={create.isPending} data-testid="commercial-coach-invoice-create-btn">
        {create.isPending ? <Loader2 size={13} className="animate-spin" /> : "Add document"}
      </Button>
    </div>
  );
}

function ContractForm({ workspaceId, currentContractId }) {
  const [title, setTitle] = useState("");
  const fileRef = useRef(null);
  const create = useCreateContract(workspaceId);

  async function handleCreate() {
    const file = fileRef.current?.files?.[0];
    if (!file || !title) return;
    try {
      await create.mutateAsync({ file, title, supersedes_ctr_id: currentContractId || undefined });
      setTitle(""); if (fileRef.current) fileRef.current.value = "";
      toast.success("Contract sent to the client");
    } catch (e) { toast.error(e.body?.detail || "Couldn't send that contract"); }
  }

  return (
    <div className="space-y-2" data-testid="commercial-coach-contract-form">
      <p className="text-xs font-semibold text-ink-soft uppercase tracking-wide">Upload Contract</p>
      <p className="text-[11px] text-ink-soft">{currentContractId ? "This uploads a new version, superseding the current contract." : "This becomes the client's first contract."}</p>
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Contract title" data-testid="commercial-coach-contract-title" />
      <input ref={fileRef} type="file" accept="application/pdf" className="text-xs" data-testid="commercial-coach-contract-file-input" />
      <Button size="sm" onClick={handleCreate} disabled={create.isPending} data-testid="commercial-coach-contract-create-btn">
        {create.isPending ? <Loader2 size={13} className="animate-spin" /> : "Upload contract"}
      </Button>
    </div>
  );
}
